import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/lib/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react'; 

export default function OtpVerificationPage() {
  const { setUser, getUser } = useAuth();
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract phone from URL just in case Context is lost on page refresh
  const searchParams = new URLSearchParams(location.search);
  const phoneParam = searchParams.get('phone');
  const targetPhone = user?.phone || phoneParam;

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    // Pre-validation
    if (otp.length < 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }

    if (!targetPhone) {
      setError("Phone number is missing. Please sign up again.");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("phone_number", targetPhone);
      formData.append("otp", otp);
      
      const res = await axios.post("https://xceededge.com/verify_otp.php", formData, { 
        headers: { "Content-Type": "multipart/form-data" } 
      });

      if (res.data && res.data.status === "success") {
        // 1. Update the user context FIRST
        setUser({ ...user, session: res.data.session, isVerified: true });
        
        // 2. Navigate to dashboard
        navigate('/dashboard');
        
      } else {
        setError(res.data.message || res.data.error || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      console.error("OTP Verification Error:", err);
      setError("A server error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10 bg-background">
      <div className="w-full max-w-md">
        
        {/* Header Section with Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mt-3">
            <img 
              src="https://www.xceededge.in/XceedTransLogo.png" 
              alt="XceedEdge Logo" 
              className="h-20 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Card Section */}
        <div className="bg-card rounded-2xl border border-border/50 shadow-xl p-6 sm:p-8 relative overflow-hidden">
          
          {/* Subtle background glow */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

          <form onSubmit={verifyOtp} className="space-y-6 relative z-10">             
            <div className='space-y-4'>
              <div className='text-center flex flex-col items-center'>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-primary" />
                </div>
                <Label className='text-xl font-heading font-bold block mb-2'>Verify Your Account</Label>
                <p className='text-sm text-muted-foreground'>
                  We sent a 6-digit code to <br/>
                  {/* FIXED: Replaced undefined targetEmail with targetPhone */}
                  <span className="font-semibold text-foreground">{targetPhone ? `+91 ${targetPhone}` : "your phone"}</span>
                </p>
              </div>

              {/* OTP Input */}
              <div className='flex justify-center py-4'>
                <InputOTP maxLength={6} value={otp} onChange={setOtp} disabled={isLoading}>
                  <InputOTPGroup className='gap-2 sm:gap-3'>
                    <InputOTPSlot index={0} className="w-10 h-12 sm:w-12 sm:h-14 text-lg border-border/50" />
                    <InputOTPSlot index={1} className="w-10 h-12 sm:w-12 sm:h-14 text-lg border-border/50" />
                    <InputOTPSlot index={2} className="w-10 h-12 sm:w-12 sm:h-14 text-lg border-border/50" />
                    <InputOTPSlot index={3} className="w-10 h-12 sm:w-12 sm:h-14 text-lg border-border/50" />
                    <InputOTPSlot index={4} className="w-10 h-12 sm:w-12 sm:h-14 text-lg border-border/50" />
                    <InputOTPSlot index={5} className="w-10 h-12 sm:w-12 sm:h-14 text-lg border-border/50" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {/* Error Message Display */}
              {error && (
                <motion.p 
                  initial={{ opacity: 0, y: -10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  className="text-red-500 text-sm text-center font-medium bg-red-500/10 py-2 rounded-lg border border-red-500/20"
                >
                  {error}
                </motion.p>
              )}
            </div>

            {/* Buttons */}
            <div className="space-y-3 pt-2">
              <Button type="submit" className="w-full h-11 text-base font-semibold" disabled={isLoading || otp.length !== 6}>
                {isLoading ? "Verifying..." : "Verify & Continue"}
              </Button>
              
              <Link to='/login' className="text-sm text-muted-foreground hover:text-primary transition-colors w-full text-center block mt-2">
                Back to login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}